import React, { useState } from 'react';
import { AlertCircle, Loader, RefreshCw } from 'lucide-react';
import MapContainer from '../map/MapContainer';
import GaushalaStatistics from '../statistics/GaushalaStatistics';
import { NormalizedGaushala, NormalizedManufacturingUnit, INDIA_CENTER } from '../../types/gaushalaMap.types';
import useGaushalaMap from '../../hooks/useGaushalaMap';
import useManufacturingMap from '../../hooks/useManufacturingMap';

/**
 * Gaushala Map Chart Component
 *
 * Main wrapper component that:
 * - Fetches gaushala data
 * - Manages map state (selected gaushala, loading, errors)
 * - Handles geocoding progress
 * - Displays map with statistics sidebar
 */

interface GaushalaMapChartProps {
  height?: string;
  showStatistics?: boolean;
  onGaushalaSelect?: (gaushala: NormalizedGaushala | null) => void;
}

const GaushalaMapChart: React.FC<GaushalaMapChartProps> = ({
  height = '600px',
  showStatistics = true,
  onGaushalaSelect,
}) => {
  const [selectedGaushala, setSelectedGaushala] =
    useState<NormalizedGaushala | null>(null);
  const [selectedManufacturingUnit, setSelectedManufacturingUnit] =
    useState<NormalizedManufacturingUnit | null>(null);
  const [filterState, setFilterState] = useState<string | null>(null);

  // Fetch gaushala data
  const {
    gaushalas,
    isLoading,
    isGeocoding,
    progress,
    error,
    statistics,
    refetch,
    filterByState,
  } = useGaushalaMap();

  // Fetch manufacturing units data
  const {
    units: manufacturingUnits,
    isLoading: isLoadingManufacturing,
    error: manufacturingError,
    refetch: refetchManufacturing,
  } = useManufacturingMap();

  const handleMarkerClick = (gaushala: NormalizedGaushala) => {
    setSelectedGaushala(gaushala);
    setSelectedManufacturingUnit(null); // Clear manufacturing selection
    if (onGaushalaSelect) {
      onGaushalaSelect(gaushala);
    }
  };

  const handleManufacturingClick = (unit: NormalizedManufacturingUnit) => {
    setSelectedManufacturingUnit(unit);
    setSelectedGaushala(null); // Clear gaushala selection
  };

  const handleStateClick = (state: string) => {
    setFilterState(filterState === state ? null : state);
  };

  // Combined refresh function
  const handleRefresh = () => {
    refetch();
    refetchManufacturing();
  };

  // Combined loading state
  const isCombinedLoading = isLoading || isLoadingManufacturing;

  // Filter gaushalas by state and exclude unknown locations (India center)
  const filteredByState = filterState
    ? filterByState(filterState)
    : gaushalas;

  // Exclude gaushalas with unknown locations (placed at India center)
  const displayGaushalas = filteredByState.filter((gaushala) => {
    // Check if gaushala is at India center (unknown location)
    const isIndiaCenter =
      Math.abs(gaushala.latitude - INDIA_CENTER.latitude) < 0.001 &&
      Math.abs(gaushala.longitude - INDIA_CENTER.longitude) < 0.001;

    // Only show gaushalas with known locations
    return !isIndiaCenter;
  });

  return (
    <div className="w-full space-y-4">
      {/* Header with Title and Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            📍 Locations Map
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Interactive map showing gaushalas and manufacturing units across India
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isCombinedLoading}
          className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors"
        >
          <RefreshCw size={18} className={isCombinedLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Geocoding Progress Indicator */}
      {isGeocoding && progress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader className="animate-spin text-blue-600" size={20} />
            <div className="flex-1">
              <p className="font-semibold text-blue-900">
                🌍 Locating Gaushalas on Map...
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Processing location data: {progress.current}/{progress.total} (
                {progress.percentage}%)
              </p>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-blue-600 h-full transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            ></div>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            ℹ️ Some gaushalas are being geo-located. This may take a moment...
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && !isGeocoding && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">
              ⚠️ Location Processing Note
            </p>
            <p className="text-sm text-amber-800 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Main Layout: Map always mounted — never replaced with a spinner.
          A loading overlay sits on top while data arrives so MapContainer
          never unmounts and the map tiles stay rendered (no blink). */}
      <div className="w-full relative z-0 isolate">
        <div
          className="bg-white rounded-lg shadow-md overflow-hidden relative"
          style={{ height }}
        >
          {/* Map is ALWAYS rendered — even with empty arrays while loading */}
          <MapContainer
            gaushalas={displayGaushalas}
            manufacturingUnits={manufacturingUnits}
            onMarkerClick={handleMarkerClick}
            onManufacturingClick={handleManufacturingClick}
            selectedGaushala={selectedGaushala}
            selectedManufacturingUnit={selectedManufacturingUnit}
            isLoading={isGeocoding}
          />

          {/* Subtle overlay while data is loading — does NOT unmount the map */}
          {isCombinedLoading && !isGeocoding && (
            <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center gap-2 pointer-events-none z-10">
              <Loader className="animate-spin text-green-600" size={28} />
              <p className="text-gray-700 text-sm font-semibold">Loading locations...</p>
            </div>
          )}

          {/* No-data message shown only after loading completes */}
          {!isCombinedLoading && displayGaushalas.length === 0 && manufacturingUnits.length === 0 && (
            <div className="absolute inset-0 bg-gray-50/90 flex flex-col items-center justify-center pointer-events-none z-10">
              <div className="text-center pointer-events-auto">
                <div className="text-5xl mb-3">📍</div>
                <p className="text-lg font-semibold text-gray-800">No locations found</p>
                <p className="text-gray-600 text-sm mt-1">
                  {filterState ? `No locations in ${filterState}. Try another state.` : 'Try refreshing or check your connection'}
                </p>
                {filterState && (
                  <button
                    onClick={() => setFilterState(null)}
                    className="mt-4 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold rounded transition-colors"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Selected Gaushala Details (Optional - Below Map) */}
      {selectedGaushala && (
        <div className="bg-white border-l-4 border-green-500 p-4 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-lg">
                🐄 {selectedGaushala.name}
              </h3>

              {/* Location Status Badge */}
              {selectedGaushala.coordinateSource === 'geocoded_from_api' ? (
                <p className="text-xs font-semibold text-green-600 mt-1">
                  ✅ EXACT Location (Automatically Geocoded)
                </p>
              ) : selectedGaushala.coordinateSource === 'user_provided' ? (
                <p className="text-xs font-semibold text-blue-600 mt-1">
                  🎯 User Provided Location
                </p>
              ) : selectedGaushala.coordinateSource === 'failed' ? (
                <p className="text-xs font-semibold text-red-600 mt-1">
                  ⚠️ Geocoding Failed - Check Address
                </p>
              ) : (
                <p className="text-xs font-semibold text-gray-600 mt-1">
                  ⏳ Location Pending Processing
                </p>
              )}

              <p className="text-sm text-gray-600 mt-1">
                📍 {selectedGaushala.address}
              </p>

              {/* Geocoded Address from API */}
              {selectedGaushala.geocodedAddress && (
                <p className="text-xs text-gray-600 mt-1 italic">
                  📌 API Matched: {selectedGaushala.geocodedAddress}
                </p>
              )}

              {selectedGaushala.pincode && (
                <p className="text-sm text-gray-600">
                  📮 Pincode: {selectedGaushala.pincode}
                </p>
              )}

              {/* Exact Coordinates Display */}
              <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                <p className="text-xs font-semibold text-gray-700">Exact Coordinates:</p>
                <p className="text-xs text-gray-600 font-mono">
                  Lat: {selectedGaushala.latitude.toFixed(6)}°N | Lng: {selectedGaushala.longitude.toFixed(6)}°E
                </p>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                🐄 Total Cattle: {selectedGaushala.metadata.totalCattle ?? 'Not provided'} | 🥛 Milking
                Cows: {selectedGaushala.metadata.milkingCows ?? 'Not provided'}
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedGaushala(null);
                if (onGaushalaSelect) {
                  onGaushalaSelect(null);
                }
              }}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Selected Manufacturing Unit Details (Optional - Below Map) */}
      {selectedManufacturingUnit && (
        <div className="bg-white border-l-4 border-blue-500 p-4 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-lg">
                🏭 {selectedManufacturingUnit.name}
              </h3>

              {/* Location Status Badge */}
              {selectedManufacturingUnit.coordinateSource === 'geocoded_from_api' ? (
                <p className="text-xs font-semibold text-green-600 mt-1">
                  ✅ EXACT Location (Automatically Geocoded)
                </p>
              ) : selectedManufacturingUnit.coordinateSource === 'user_provided' ? (
                <p className="text-xs font-semibold text-blue-600 mt-1">
                  🎯 User Provided Location
                </p>
              ) : selectedManufacturingUnit.coordinateSource === 'failed' ? (
                <p className="text-xs font-semibold text-red-600 mt-1">
                  ⚠️ Geocoding Failed
                </p>
              ) : (
                <p className="text-xs font-semibold text-gray-600 mt-1">
                  ⏳ Location Pending
                </p>
              )}

              <p className="text-sm text-gray-600 mt-1">
                📍 {selectedManufacturingUnit.address}
              </p>

              {/* Geocoded Address from API */}
              {selectedManufacturingUnit.geocodedAddress && (
                <p className="text-xs text-gray-600 mt-1 italic">
                  📌 API Matched: {selectedManufacturingUnit.geocodedAddress}
                </p>
              )}

              {selectedManufacturingUnit.unitCode && (
                <p className="text-sm text-gray-600">
                  🏷️ Unit Code: {selectedManufacturingUnit.unitCode}
                </p>
              )}

              {/* Exact Coordinates Display */}
              <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                <p className="text-xs font-semibold text-gray-700">Exact Coordinates:</p>
                <p className="text-xs text-gray-600 font-mono">
                  Lat: {selectedManufacturingUnit.latitude.toFixed(6)}°N | Lng: {selectedManufacturingUnit.longitude.toFixed(6)}°E
                </p>
              </div>

              <div className="text-xs text-gray-500 mt-2 space-y-1">
                {selectedManufacturingUnit.metadata.capacity ? (
                  <p>⚙️ Capacity: {selectedManufacturingUnit.metadata.capacity} units</p>
                ) : (
                  <p>⚙️ Capacity: Not provided</p>
                )}
                {selectedManufacturingUnit.metadata.status ? (
                  <p>📊 Status: {selectedManufacturingUnit.metadata.status}</p>
                ) : (
                  <p>📊 Status: Not provided</p>
                )}
              </div>
            </div>
            <button
              onClick={() => setSelectedManufacturingUnit(null)}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GaushalaMapChart;
